/**
 * Person Detail Page - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { ArrowPathIcon, ExclamationTriangleIcon, UserCircleIcon, PencilIcon, ArrowLeftIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import PersonForm from '../components/forms/PersonForm';
import JournalForm from '../components/forms/JournalForm';
import RelatedItemsTabs from '../components/ui/RelatedItemsTabs';
import { useJournalModal } from '../hooks/useJournalModal';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Panel, Button, Badge, Divider } from '../design/primitives';
import { usePhone } from '../contexts/PhoneContext';

interface Person {
  id: number;
  name: string;
  relationship?: string;
  notes?: string;
  appreciation?: string;
  triggers?: string;
  last_interaction?: string;
  tasks?: any[];
  projects?: any[];
  goals?: any[];
  journal_entries?: any[];
  emails?: any[];
  meetings?: any[];
  email_addresses?: Array<{ id: number; email: string; label?: string }>;
  phone_numbers?: Array<{ id: number; phone: string; label?: string }>;
}

const PersonDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [person, setPerson] = useState<Person | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updatingTaskId, setUpdatingTaskId] = useState<number | null>(null);
  const [updatingEmailId, setUpdatingEmailId] = useState<string | null>(null);
  const { makeCall, callState, isReady } = usePhone();

  const fetchPerson = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/api/people/${id}/`);
      setPerson(response.data);
    } catch (err) {
      console.error("Error fetching person:", err);
      setError("Failed to load person details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const {
    isJournalModalOpen,
    selectedJournalEntry,
    handleJournalEntryClick,
    handleJournalEntryUpdated,
    handleCloseJournal,
  } = useJournalModal(fetchPerson);

  useEffect(() => {
    if (id) {
      fetchPerson();
    }
  }, [id, fetchPerson]);

  const handlePersonUpdated = (updatedPerson: Person) => {
    setPerson(updatedPerson);
    setIsEditModalOpen(false);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return null;
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch {
      return "Invalid Date";
    }
  };

  const handleTaskToggle = async (taskId: number, currentStatus: boolean) => {
    setUpdatingTaskId(taskId);
    try {
      await apiClient.patch(`/api/tasks/${taskId}/`, { is_done: !currentStatus });
      await fetchPerson();
    } catch (err) {
      console.error('Failed to update task:', err);
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const handleCall = (phoneNumber: string) => {
    makeCall(phoneNumber, person?.id);
  };

  const handleEmailStatusChange = async (emailId: string, isHandled: boolean) => {
    setUpdatingEmailId(emailId);
    try {
      if (isHandled) {
        await apiClient.post(`/api/emails/${emailId}/mark_handled/`);
      } else {
        await apiClient.patch(`/api/emails/${emailId}/`, { is_handled: false });
      }
      await fetchPerson();
    } catch (err) {
      console.error('Failed to update email status:', err);
    } finally {
      setUpdatingEmailId(null);
    }
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: COLORS.bg.base,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[24]
      }}>
        <ArrowPathIcon style={{
          width: '24px',
          height: '24px',
          color: COLORS.text.tertiary,
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (error || !person) {
    return (
      <div style={{
        backgroundColor: COLORS.bg.base,
        minHeight: '100vh',
        padding: SPACING[24]
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <Panel style={{ textAlign: 'center', padding: SPACING[48] }}>
            <ExclamationTriangleIcon style={{
              width: '48px',
              height: '48px',
              color: COLORS.status.error,
              margin: '0 auto',
              marginBottom: SPACING[16]
            }} />
            <h2 style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              margin: 0,
              marginBottom: SPACING[8]
            }}>
              Error
            </h2>
            <p style={{
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.text.secondary,
              margin: 0,
              marginBottom: SPACING[16]
            }}>
              {error || "Person not found"}
            </p>
            <button
              onClick={() => navigate('/people')}
              style={{
                background: 'none',
                border: 'none',
                color: COLORS.accent.default,
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                cursor: 'pointer',
                padding: 0
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = COLORS.accent.hover}
              onMouseLeave={(e) => e.currentTarget.style.color = COLORS.accent.default}
            >
              Return to People
            </button>
          </Panel>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: COLORS.bg.base,
      minHeight: '100vh',
      padding: SPACING[24]
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <button
          onClick={() => navigate('/people')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: SPACING[4],
            background: 'none',
            border: 'none',
            color: COLORS.text.secondary,
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            cursor: 'pointer',
            marginBottom: SPACING[24],
            padding: 0
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = COLORS.text.primary}
          onMouseLeave={(e) => e.currentTarget.style.color = COLORS.text.secondary}
        >
          <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          Back to People
        </button>

        {/* Person Header */}
        <Panel style={{ marginBottom: SPACING[32] }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: SPACING[16]
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16] }}>
              <div style={{
                padding: SPACING[12],
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '50%'
              }}>
                <UserCircleIcon style={{
                  width: '32px',
                  height: '32px',
                  color: COLORS.accent.default
                }} />
              </div>
              <div>
                <h1 style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.headings.sizes.h1,
                  fontWeight: TYPOGRAPHY.headings.weight,
                  color: COLORS.text.primary,
                  margin: 0,
                  marginBottom: SPACING[4]
                }}>
                  {person.name}
                </h1>
                {person.relationship && (
                  <p style={{
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    color: COLORS.text.secondary,
                    margin: 0
                  }}>
                    {person.relationship}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={() => setIsEditModalOpen(true)}
              icon={<PencilIcon style={{ width: '16px', height: '16px' }} />}
            >
              Edit
            </Button>
          </div>

          <Divider />

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: SPACING[24]
          }}>
            {person.appreciation && (
              <div>
                <dt style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: SPACING[8]
                }}>
                  Appreciation
                </dt>
                <dd style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.primary,
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  {person.appreciation}
                </dd>
              </div>
            )}

            {person.triggers && (
              <div>
                <dt style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: SPACING[8]
                }}>
                  Triggers
                </dt>
                <dd style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.primary,
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  {person.triggers}
                </dd>
              </div>
            )}

            {person.notes && (
              <div>
                <dt style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: SPACING[8]
                }}>
                  Notes
                </dt>
                <dd style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.primary,
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  {person.notes}
                </dd>
              </div>
            )}

            {person.last_interaction && (
              <div>
                <dt style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: SPACING[8]
                }}>
                  Last Interaction
                </dt>
                <dd style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.primary,
                  margin: 0
                }}>
                  {formatDate(person.last_interaction)}
                </dd>
              </div>
            )}

            {person.email_addresses && person.email_addresses.length > 0 && (
              <div>
                <dt style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: SPACING[8]
                }}>
                  Email Addresses
                </dt>
                <dd style={{ margin: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING[8] }}>
                    {person.email_addresses.map((emailObj) => (
                      <span
                        key={emailObj.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: SPACING[8],
                          padding: `${SPACING[4]} ${SPACING[12]}`,
                          backgroundColor: COLORS.bg.hover,
                          borderRadius: RADIUS,
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.default,
                          color: COLORS.text.primary
                        }}
                      >
                        {emailObj.email}
                        {emailObj.label && (
                          <span style={{
                            fontSize: TYPOGRAPHY.body.sizes.small,
                            color: COLORS.text.secondary
                          }}>
                            ({emailObj.label})
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </dd>
              </div>
            )}

            {person.phone_numbers && person.phone_numbers.length > 0 && (
              <div>
                <dt style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: SPACING[8]
                }}>
                  Phone Numbers
                </dt>
                <dd style={{ margin: 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
                    {person.phone_numbers.map((phoneObj) => (
                      <div
                        key={phoneObj.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: SPACING[8],
                          padding: `${SPACING[4]} ${SPACING[12]}`,
                          backgroundColor: COLORS.bg.hover,
                          borderRadius: RADIUS,
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.default,
                          color: COLORS.text.primary,
                          width: 'fit-content'
                        }}
                      >
                        <PhoneIcon style={{ width: '14px', height: '14px', color: COLORS.text.secondary }} />
                        {phoneObj.phone}
                        {phoneObj.label && (
                          <span style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>
                            ({phoneObj.label})
                          </span>
                        )}
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleCall(phoneObj.phone)}
                          disabled={callState !== 'idle' || !isReady}
                        >
                          Call
                        </Button>
                      </div>
                    ))}
                  </div>
                </dd>
              </div>
            )}
          </div>
        </Panel>

        {/* Related Items Tabs */}
        <RelatedItemsTabs
          tasks={person.tasks}
          projects={person.projects}
          goals={person.goals}
          emails={person.emails}
          meetings={person.meetings}
          journalEntries={person.journal_entries}
          onTaskToggle={handleTaskToggle}
          onEmailStatusChange={handleEmailStatusChange}
          onJournalEntryClick={handleJournalEntryClick}
          isUpdatingTask={updatingTaskId}
          isUpdatingEmail={updatingEmailId}
        />

        {isEditModalOpen && (
          <PersonForm
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onPersonCreated={handlePersonUpdated}
            initialPerson={person}
            isEditMode={true}
          />
        )}

        {isJournalModalOpen && selectedJournalEntry && (
          <JournalForm
            isOpen={isJournalModalOpen}
            onClose={handleCloseJournal}
            onJournalEntryCreated={handleJournalEntryUpdated}
            onJournalEntryUpdated={handleJournalEntryUpdated}
            initialEntry={selectedJournalEntry}
            isEditMode={true}
          />
        )}
      </div>
    </div>
  );
};

export default PersonDetailPage;
